import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Card,
  Group,
  Text,
  Image,
  Stack,
  Button,
  Loader,
  Center,
  Alert,
  Badge,
  Divider,
  Title,
  Paper,
  Anchor,
  SegmentedControl,
  Select,
  Box,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconAlertCircle,
  IconExternalLink,
  IconPlus,
} from '@tabler/icons-react';
import { useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { useAuth } from '../../context/AuthContext';
import { productsService } from '../../services/products';
import { reviewsService } from '../../services/reviews';
import { ReviewCard, StarRating } from '../../components/reviews';
import type { Review } from '../../types';

const SORT_OPTIONS = [
  { label: 'Newest', value: 'newest' },
  { label: 'Oldest', value: 'oldest' },
  { label: 'Highest Rated', value: 'highest_rated' },
  { label: 'Lowest Rated', value: 'lowest_rated' },
  { label: 'Most Helpful', value: 'most_helpful' },
];

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const isMobile = !useMediaQuery('(min-width: 576px)');

  const [sort, setSort] = useState('newest');

  const { data: product, isLoading: isLoadingProduct, error: productError } = useQuery({
    queryKey: ['product', id],
    queryFn: () => productsService.getById(id!),
    enabled: !!id,
    staleTime: 60_000,
  });

  const { data: amazonLinkData } = useQuery({
    queryKey: ['product-amazon-link', id],
    queryFn: () => productsService.getAmazonLink(id!),
    enabled: !!id && !product?.amazonAffiliateUrl,
    staleTime: 24 * 60 * 60 * 1000,
  });

  const amazonUrl = product?.amazonAffiliateUrl ?? amazonLinkData?.affiliateUrl;
  const displayImageUrl = product?.imageUrl ?? amazonLinkData?.imageUrl;

  const {
    data: reviewsData,
    isLoading: isLoadingReviews,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['product-reviews', id, sort],
    queryFn: ({ pageParam }) => reviewsService.getByProductId(id!, pageParam, 10, sort),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.hasNextPage ? lastPage.page + 1 : undefined,
    enabled: !!id,
    staleTime: 60_000,
  });

  const reviews: Review[] = reviewsData?.pages.flatMap((p) => p.items) ?? [];
  const totalReviewCount = reviewsData?.pages[0]?.totalCount ?? 0;

  const sentinelRef = useInfiniteScroll(fetchNextPage, !!hasNextPage, isFetchingNextPage);

  const handleSortChange = (value: string) => {
    setSort(value);
  };

  const handleDeleteReview = (reviewId: string) => {
    queryClient.setQueryData(['product-reviews', id, sort], (old: any) => {
      if (!old) return old;
      return {
        ...old,
        pages: old.pages.map((page: any) => ({
          ...page,
          items: page.items.filter((r: Review) => r.id !== reviewId),
          totalCount: Math.max(0, page.totalCount - 1),
        })),
      };
    });
  };

  const isLoading = isLoadingProduct;

  if (isLoading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (productError || !product) {
    return (
      <Container fluid px={{ base: 'md', md: 'xl' }} pt="md" pb="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
          {productError ? 'Failed to load product. Please try again.' : 'Product not found'}
          <Button variant="subtle" size="xs" mt="xs" onClick={() => navigate(-1)}>
            Go back
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid px={{ base: 'md', md: 'xl' }} pt="md" pb="xl">
      {/* Product Card */}
      <Card shadow="sm" padding="lg" radius="md" withBorder mb="xl">
        <Stack gap="md">
          {/* Image + info — side by side on desktop, stacked on mobile */}
          <Group align="flex-start" gap="lg" wrap={isMobile ? 'wrap' : 'nowrap'}>
            {displayImageUrl ? (
              <Box style={{ flexShrink: 0, width: isMobile ? '100%' : 200, textAlign: isMobile ? 'center' : 'left' }}>
                <Image
                  src={displayImageUrl}
                  alt={product.name}
                  w={isMobile ? 160 : 200}
                  h={isMobile ? 160 : 200}
                  radius="md"
                  fit="contain"
                  style={{ margin: isMobile ? '0 auto' : undefined }}
                />
              </Box>
            ) : (
              <Paper w={isMobile ? '100%' : 200} h={isMobile ? 120 : 200} bg="gray.1" radius="md" style={{ flexShrink: 0 }}>
                <Center h="100%">
                  <Text c="dimmed">No image</Text>
                </Center>
              </Paper>
            )}

          <Stack style={{ flex: 1 }} gap="sm">
            {product.brand && (
              <Text size="sm" c="dimmed" tt="uppercase" fw={500}>
                {product.brand}
              </Text>
            )}

            <Title order={2}>{product.name}</Title>

            {product.category && (
              <Badge variant="light" size="lg">
                {product.category}
              </Badge>
            )}

            {/* Rating summary */}
            {totalReviewCount > 0 && product.averageRating && (
              <Group gap="xs">
                <StarRating rating={product.averageRating} size="md" showValue />
                <Text c="dimmed" size="sm">
                  ({totalReviewCount} {totalReviewCount === 1 ? 'review' : 'reviews'})
                </Text>
              </Group>
            )}

            {totalReviewCount === 0 && !isLoadingReviews && (
              <Text c="dimmed" size="sm">
                No reviews yet
              </Text>
            )}

            {product.description && (
              <Text size="sm" c="dimmed">
                {product.description}
              </Text>
            )}

            {/* Write review button */}
            {isAuthenticated && !product.currentUserHasReviewed && (
              <Button
                leftSection={<IconPlus size={16} />}
                component={Link}
                to={`/reviews/create?productId=${product.id}`}
                size="md"
              >
                Write a Review
              </Button>
            )}

            {/* Where to Buy */}
            <div>
              <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={6}>Where to Buy</Text>
              <Group gap="xs">
                {amazonUrl && (
                  <Anchor href={amazonUrl} target="_blank" rel="noopener noreferrer sponsored">
                    <Button variant="filled" color="orange" leftSection={<IconExternalLink size={14} />} size="sm">
                      Amazon (paid link)
                    </Button>
                  </Anchor>
                )}
                <Anchor
                  href={`https://www.costco.com/CatalogSearch?keyword=${encodeURIComponent(product.barcode || product.name)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" color="gray" leftSection={<IconExternalLink size={14} />} size="sm">
                    Costco
                  </Button>
                </Anchor>
              </Group>
            </div>
          </Stack>
          </Group>
        </Stack>
      </Card>

      {/* Reviews Section */}
      <Group justify="space-between" align="center" my="xl" wrap="nowrap">
        <Divider label={`Reviews (${totalReviewCount})`} labelPosition="left" style={{ flex: 1, minWidth: 0 }} />
        {isMobile ? (
          <Select
            size="xs"
            value={sort}
            onChange={(v) => handleSortChange(v ?? 'newest')}
            data={SORT_OPTIONS}
            style={{ width: 140, flexShrink: 0 }}
            comboboxProps={{ withinPortal: true }}
          />
        ) : (
          <SegmentedControl
            size="xs"
            value={sort}
            onChange={handleSortChange}
            data={SORT_OPTIONS}
          />
        )}
      </Group>

      <Stack gap="md">
        {reviews.map((review) => (
          <ReviewCard
            key={review.id}
            review={review}
            showProduct={false}
            onDelete={handleDeleteReview}
          />
        ))}

        {(isLoadingReviews || isFetchingNextPage) && (
          <Center py="md">
            <Loader size="sm" />
          </Center>
        )}
        <div ref={sentinelRef} />

        {reviews.length === 0 && !isLoadingReviews && (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <Text c="dimmed">No reviews yet. Be the first to share your thoughts!</Text>
              {isAuthenticated && !product.currentUserHasReviewed && (
                <Button
                  leftSection={<IconPlus size={16} />}
                  component={Link}
                  to={`/reviews/create?productId=${product.id}`}
                >
                  Write a Review
                </Button>
              )}
            </Stack>
          </Center>
        )}
      </Stack>
    </Container>
  );
}
